import React from 'react';
import { Link } from 'react-router-dom';
import { useMonthlyProcessList } from './hooks/useMonthlyProcessList';
import SharedFilters from '../../../components/SharedFilters';
import AppPagination from '../../../components/AppPagination';
import MonthlyAttendanceTable from './components/MonthlyAttendanceTable';
import PredefinedReportsDrawer from '../../../components/PredefinedReportsDrawer';

export default function MonthlyProcessListScreen() {
  const { state, actions } = useMonthlyProcessList();

  const statusTabs = [
    { key: 'All', label: 'All' },
    { key: 'Processed', label: 'Processed' },
    { key: 'Pending', label: 'Pending' },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Monthly Attendance
          </h1>
          <p className="text-xs text-on-surface-variant font-medium mt-1">
            Process, review and rollback monthly attendance summaries for payroll generation.
          </p>
        </div>

        {/* Actions Cluster: Status Tabs + Run Process CTA */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Segmented Status Tabs */}
          <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => actions.handleStatusTabChange(tab.key)}
                className={`px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${
                  state.statusFilter === tab.key
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Primary CTA */}
          <Link
            to="/payroll/monthly-process/run"
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-5 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition-all select-none cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">event_repeat</span>
            <span>Run Monthly Process</span>
          </Link>
        </div>
      </section>

      {/* Shared Filters */}
      <SharedFilters
        searchText={state.searchText}
        setSearchText={actions.setSearchText}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        departmentFilter={state.departmentFilter}
        employeeFilter={state.employeeFilter}
        groupFilter={state.empGroupFilter}
        genderFilter={state.genderFilter}
        statusFilter={state.statusFilter}
        monthFilter={state.monthFilter}
        yearFilter={state.yearFilter}
        companyOptions={state.companyOptions}
        branchOptions={state.branchOptions}
        departmentOptions={state.departmentOptions}
        groupOptions={state.groupOptions}
        genderOptions={state.genderOptions}
        monthOptions={state.monthOptions}
        yearOptions={state.yearOptions}
        showStatus={false}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        setDepartmentFilter={actions.setDepartmentFilter}
        setEmployeeFilter={actions.setEmployeeFilter}
        setGroupFilter={actions.setEmpGroupFilter}
        setGenderFilter={actions.setGenderFilter}
        setMonthFilter={actions.setMonthFilter}
        setYearFilter={actions.setYearFilter}
        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}
        visibleAccordionFilters={[
          'company',
          'branch',
          'department',
          'employee',
          'group',
          'gender',
          'monthYear',
        ]}
        tools={state.tools}
        onToolSelect={actions.handleToolSelect}
      />

      {/* Table Section */}
      <section className="space-y-4">
        <MonthlyAttendanceTable
          records={state.records}
          isLoading={state.isLoading}
          onDelete={actions.handleDelete}
          onResetFilters={actions.resetFilters}
        />

        {/* Pagination Footer */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing{' '}
            {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>

      {/* Predefined Reports Drawer */}
      <PredefinedReportsDrawer
        isOpen={state.isReportsDrawerOpen}
        onClose={() => actions.setIsReportsDrawerOpen(false)}
        title="Monthly Attendance Reports"
        reports={state.predefinedReports}
        onAction={actions.handleReportAction}
      />
    </div>
  );
}
